// Dashboard API client — talks to our Hono API.

import { authFetch } from "../../auth/authFetch";

/**
 * `??` not `||` on purpose: an explicitly empty VITE_APP_API_URL means
 * "same origin" (requests go to /api/... on this host). Only an *unset*
 * variable falls back to the local API.
 */
const API_BASE = import.meta.env.VITE_APP_API_URL ?? "http://localhost:8787";

export interface BoardItem {
  id: string;
  name: string;
  icon: string;
  thumbnailUrl: string | null;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string;
  isStarred: boolean;
  isDeleted?: boolean;
  workspace: { id: string; name: string };
  creator: { id: string; name: string; avatar: string | null };
}

export interface WorkspaceItem {
  id: string;
  name: string;
  /** Personal workspace — can't be deleted. */
  isDefault?: boolean;
  /** The signed-in user's role in this workspace. */
  role?: "owner" | "admin" | "editor" | "viewer";
}

export interface Member {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: "owner" | "admin" | "editor" | "viewer";
}

export async function fetchBoards(): Promise<{
  boards: BoardItem[];
  total: number;
}> {
  const res = await authFetch(`${API_BASE}/api/boards`);
  if (!res.ok) throw new Error("Failed to fetch boards");
  return res.json();
}

/**
 * Throw with the server's own wording when it sent any — plan limits
 * ("The Free plan is limited to 3 boards…") and invite errors ("No Chartoon
 * account with that email") are written for the user, so don't bury them.
 */
async function throwServerError(
  res: Response,
  fallback: string,
): Promise<never> {
  const body = (await res.json().catch(() => null)) as {
    message?: string;
    error?: string;
  } | null;
  throw new Error(body?.message ?? body?.error ?? fallback);
}

export async function createBoard(workspaceId?: string): Promise<BoardItem> {
  const res = await authFetch(`${API_BASE}/api/boards`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspaceId }),
  });
  if (!res.ok) await throwServerError(res, "Failed to create board");
  const { board } = await res.json();
  return board;
}

export async function renameBoard(id: string, name: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to rename board");
}

export async function deleteBoard(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete board");
}

export async function deleteBoardForever(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}/permanent`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to permanently delete board");
}

export async function restoreBoard(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}/restore`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to restore board");
}

export async function duplicateBoard(id: string): Promise<BoardItem> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}/duplicate`, {
    method: "POST",
  });
  if (!res.ok) await throwServerError(res, "Failed to duplicate board");
  const { board } = await res.json();
  return board;
}

export async function moveBoard(
  id: string,
  workspaceId: string,
): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspaceId }),
  });
  if (!res.ok) throw new Error("Failed to move board");
}

export async function fetchWorkspaces(): Promise<WorkspaceItem[]> {
  const res = await authFetch(`${API_BASE}/api/workspaces`);
  if (!res.ok) throw new Error("Failed to fetch workspaces");
  const { workspaces } = await res.json();
  return workspaces;
}

export async function createWorkspace(name: string): Promise<WorkspaceItem> {
  const res = await authFetch(`${API_BASE}/api/workspaces`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  // surfaces the 402 plan_limit copy, same as board creation
  if (!res.ok) await throwServerError(res, "Failed to create workspace");
  const { workspace } = await res.json();
  return workspace;
}

export async function renameWorkspace(id: string, name: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/workspaces/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to rename workspace");
}

export async function deleteWorkspace(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/workspaces/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete workspace");
}

async function memberRequest(url: string, init?: RequestInit): Promise<void> {
  const res = await authFetch(url, init);
  if (!res.ok) await throwServerError(res, "Request failed");
}

export async function fetchMembers(workspaceId: string): Promise<Member[]> {
  const res = await authFetch(
    `${API_BASE}/api/workspaces/${workspaceId}/members`,
  );
  if (!res.ok) throw new Error("Failed to load members");
  const { members } = await res.json();
  return members;
}

export async function addMember(
  workspaceId: string,
  email: string,
  role: string,
): Promise<void> {
  await memberRequest(`${API_BASE}/api/workspaces/${workspaceId}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
}

export async function updateMemberRole(
  workspaceId: string,
  userId: string,
  role: string,
): Promise<void> {
  await memberRequest(
    `${API_BASE}/api/workspaces/${workspaceId}/members/${userId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    },
  );
}

export async function removeMember(
  workspaceId: string,
  userId: string,
): Promise<void> {
  await memberRequest(
    `${API_BASE}/api/workspaces/${workspaceId}/members/${userId}`,
    { method: "DELETE" },
  );
}

export type TemplateStatus = "private" | "pending" | "approved" | "rejected";

/** Where a new template goes: kept to the owner, or submitted to the public
 *  gallery for Chartoon to review. */
export type TemplateVisibility = "private" | "community";

export interface TemplateItem {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string | null;
  /** Published by Chartoon admins — visible to everyone, read-only. */
  isGlobal: boolean;
  /** Owned by the signed-in user — the only ones they can manage. */
  isMine: boolean;
  /** Hearted by the signed-in user. Server state, follows the account. */
  isFavorite: boolean;
  status: TemplateStatus;
  authorName: string;
  /** Why a submission was turned down (own templates only). */
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TemplateScope = "all" | "mine" | "global" | "community" | "favorites";

export interface TemplateList {
  templates: TemplateItem[];
  total: number;
  page: number;
  pageSize: number;
}

export async function fetchTemplates(opts: {
  page?: number;
  pageSize?: number;
  q?: string;
  scope?: TemplateScope;
}): Promise<TemplateList> {
  const params = new URLSearchParams();
  if (opts.page) params.set("page", String(opts.page));
  if (opts.pageSize) params.set("pageSize", String(opts.pageSize));
  if (opts.q) params.set("q", opts.q);
  if (opts.scope && opts.scope !== "all") params.set("scope", opts.scope);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/api/templates${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error("Failed to fetch templates");
  return res.json();
}

export async function createTemplateFromBoard(input: {
  boardId: string;
  name?: string;
  description?: string;
  visibility?: TemplateVisibility;
}): Promise<TemplateItem> {
  const res = await authFetch(`${API_BASE}/api/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwServerError(res, "Failed to save as template");
  const { template } = await res.json();
  return template;
}

/** Upload a parsed .excalidraw payload as a template. `visibility:
 *  "community"` submits it to the Chartoon gallery for review. */
export async function uploadTemplate(input: {
  name: string;
  description?: string;
  visibility?: TemplateVisibility;
  elements: unknown[];
  appState?: Record<string, unknown>;
  files?: Record<string, unknown>;
}): Promise<TemplateItem> {
  const res = await authFetch(`${API_BASE}/api/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwServerError(res, "Failed to upload template");
  const { template } = await res.json();
  return template;
}

export async function renameTemplate(id: string, name: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to rename template");
}

/** Submit an existing template to the gallery, or pull it back. Returns the
 *  updated template (status flips to "pending" / "private"). */
export async function setTemplateVisibility(
  id: string,
  visibility: TemplateVisibility,
): Promise<TemplateItem> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visibility }),
  });
  if (!res.ok) await throwServerError(res, "Failed to update the template");
  const { template } = await res.json();
  return template;
}

/** Heart / unheart a template. Idempotent; 404s when the template isn't
 *  visible to the caller. */
export async function setTemplateFavorite(
  id: string,
  favorite: boolean,
): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}/favorite`, {
    method: favorite ? "PUT" : "DELETE",
  });
  if (!res.ok) {
    throw new Error(
      favorite ? "Couldn't add to favorites" : "Couldn't remove from favorites",
    );
  }
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete template");
}

/** A template's raw scene, for stamping into the board already being edited
 *  (the canvas library panel) rather than creating a new one. Unlike
 *  `useTemplate` this creates nothing, so it never hits the board cap. */
export interface TemplateScene {
  name: string;
  scene: {
    elements: readonly any[];
    appState?: Record<string, any>;
    /** image blobs are inlined as dataURLs (the legacy scene format) */
    files?: Record<string, any>;
  };
}

export async function fetchTemplateScene(
  id: string,
  signal?: AbortSignal,
): Promise<TemplateScene> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}/scene`, {
    signal,
  });
  if (!res.ok) await throwServerError(res, "Failed to load template");
  return res.json();
}

/** Stamp the template into a new board; caller opens the returned board. */
export async function useTemplate(
  id: string,
  workspaceId?: string,
): Promise<BoardItem> {
  const res = await authFetch(`${API_BASE}/api/templates/${id}/use`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspaceId }),
  });
  // 402 board-cap copy is written for the user — surface it verbatim
  if (!res.ok) await throwServerError(res, "Failed to use template");
  const { board } = await res.json();
  return board;
}

export async function deleteAccount(): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/me`, { method: "DELETE" });
  if (!res.ok) await throwServerError(res, "Failed to delete account");
}

/**
 * Create a board on the API, then open it in the canvas.
 *
 * Throws on failure rather than opening `?boardId=new`: that URL is a
 * local-only scratch canvas (see getCloudBoardId in data/cloudBoard.ts), so
 * falling back to it silently hands the user a board that is never saved —
 * including on the common path where the Free plan's board cap returns 402.
 * Callers surface the error instead.
 */
export async function createAndOpenBoard(workspaceId?: string): Promise<void> {
  const board = await createBoard(workspaceId);
  window.location.href = getCanvasUrl(board.id);
}

export async function starBoard(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}/star`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to star board");
}

export async function unstarBoard(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/boards/${id}/star`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to unstar board");
}

export interface UsageCounter {
  used: number;
  /** null = unlimited (Creator). */
  limit: number | null;
  resetAt?: string | null;
}

export interface Me {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  plan: "free" | "pro";
  prefs: Record<string, unknown>;
  /** Server-side: Stripe keys + prices are all set. Anything but `true` (an
   *  older API, a stale cached profile) means checkout can only 503, so the
   *  upgrade CTAs stay hidden rather than offer a dead button. */
  billingEnabled?: boolean;
  usage?: { boards: UsageCounter; exports: UsageCounter };
  subscription?: {
    status: string;
    interval: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

/** What users see; the API keeps the internal enum value `pro`. */
export const PLAN_DISPLAY: Record<Me["plan"], string> = {
  free: "Free",
  pro: "Creator",
};

export async function fetchMe(): Promise<Me> {
  const res = await authFetch(`${API_BASE}/api/me`);
  if (!res.ok) throw new Error("Failed to fetch profile");
  return res.json();
}

export async function updateMe(patch: {
  name?: string;
  avatarUrl?: string;
  prefs?: Record<string, unknown>;
}): Promise<Me> {
  const res = await authFetch(`${API_BASE}/api/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update profile");
  return res.json();
}

/**
 * Upload an avatar to Supabase Storage (fixed path avatars/<uid>/avatar,
 * upsert) and persist the public URL on the profile. `?v=` busts caches since
 * the object path never changes.
 */
export async function uploadAvatar(file: File, userId: string): Promise<Me> {
  const { supabase } = await import("../../auth/supabase");
  const path = `${userId}/avatar`;
  const { error } = await supabase.storage
    .from("avatars")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from("avatars").getPublicUrl(path);
  return updateMe({ avatarUrl: `${data.publicUrl}?v=${Date.now()}` });
}

/**
 * Everything the user owns (boards, workspaces, scenes) as one JSON blob.
 * Pro-only — a Free plan gets 403 with the upgrade message, which is worth
 * showing verbatim.
 */
export async function fetchExport(): Promise<unknown> {
  const res = await authFetch(`${API_BASE}/api/export`);
  if (!res.ok) await throwServerError(res, "Failed to export data");
  return res.json();
}

/** Send the user to Stripe Checkout for the Creator plan. */
export async function startCheckout(interval: "month" | "year"): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/billing/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interval }),
  });
  if (!res.ok)
    await throwServerError(res, "Couldn't start checkout. Please try again.");
  const { url } = await res.json();
  window.location.href = url;
}

/** Send the user to the Stripe Customer Portal (cancel, card, invoices). */
export async function openBillingPortal(): Promise<void> {
  const res = await authFetch(`${API_BASE}/api/billing/portal`, {
    method: "POST",
  });
  if (!res.ok)
    await throwServerError(res, "Couldn't open billing. Please try again.");
  const { url } = await res.json();
  window.location.href = url;
}

/**
 * The canvas is served from this same origin (see the multi-page build in
 * vite.config.mts; `/board/*` reaches index.html via the SPA fallback), so
 * this is a plain relative URL. Mirrors `boardPath` in data/cloudBoard.ts —
 * that's the parser for these URLs; keep the two in step.
 */
export function getCanvasUrl(boardId: string): string {
  return `/board/${encodeURIComponent(boardId)}`;
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  linkUrl: string | null;
  createdAt: string;
}

export interface NotificationFeed {
  notifications: NotificationItem[];
  unreadCount: number;
  /** Everything created after this is "unread"; null = all unread. */
  seenAt: string | null;
}

export async function fetchNotifications(): Promise<NotificationFeed> {
  const res = await authFetch(`${API_BASE}/api/notifications`);
  if (!res.ok) throw new Error("Failed to fetch notifications");
  return res.json();
}

export async function markNotificationsSeen(): Promise<void> {
  await authFetch(`${API_BASE}/api/notifications/seen`, { method: "POST" });
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
